import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { studentService } from '../services/studentService';
import { classService } from '../services/classService';
import { schoolService } from '../services/schoolService';
import { Student, StudentRequest, Class, School, StudentClassEnrollment, EnrollmentRequest } from '../types';
import { PlusIcon, SearchIcon, UsersIcon } from '../components/Icons';
import { useToast } from '../context/ToastContext';

const GRADE_DISPLAY_MAP: Record<string, string> = {
  '七年级': '七年级（初一）',
  '八年级': '八年级（初二）',
  '九年级': '九年级（初三）',
};

const getGradeDisplay = (grade: string) => GRADE_DISPLAY_MAP[grade] || grade;

const StudentListPage: React.FC = () => {
  const navigate = useNavigate();
  const { schoolId, year, classId } = useParams<{ schoolId: string; year: string; classId?: string }>();
  const { showToast } = useToast();

  const [school, setSchool] = useState<School | null>(null);
  const [currentClass, setCurrentClass] = useState<Class | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name,asc');

  // 班级筛选
  const [showClassFilter, setShowClassFilter] = useState(false);
  const [allClasses, setAllClasses] = useState<Class[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string | undefined>(classId);

  // 表单相关
  const [showModal, setShowModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [formData, setFormData] = useState<StudentRequest>({
    idCardNumber: '',
    name: '',
    studentNumber: '',
    gender: undefined,
    birthDate: '',
    phone: '',
    parentName: '',
    parentPhone: '',
    schoolId: parseInt(schoolId || '0'),
    classId: classId || '',
    notes: '',
  });

  // 密码管理相关
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [newPassword, setNewPassword] = useState('');

  // 就读历史相关
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [enrollmentStudent, setEnrollmentStudent] = useState<Student | null>(null);
  const [enrollments, setEnrollments] = useState<StudentClassEnrollment[]>([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [showAddEnrollment, setShowAddEnrollment] = useState(false);
  const [enrollmentForm, setEnrollmentForm] = useState<EnrollmentRequest>({ classId: '', academicYear: '', notes: '' });

  // 移出班级/删除确认相关
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Student | null>(null);
  const [removing, setRemoving] = useState(false);

  // 密码重置确认
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetTarget, setResetTarget] = useState<Student | null>(null);

  // 关联已有学生相关
  const [showAssociateModal, setShowAssociateModal] = useState(false);
  const [associateIdCard, setAssociateIdCard] = useState('');
  const [associateStudent, setAssociateStudent] = useState<Student | null>(null);
  const [associateSearching, setAssociateSearching] = useState(false);
  const [associateError, setAssociateError] = useState('');
  const [associating, setAssociating] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // 操作下拉菜单
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, right: 0 });

  // 点击外部关闭下拉菜单
  useEffect(() => {
    if (openActionMenu === null) return;
    const handleClickOutside = () => setOpenActionMenu(null);
    // 用 setTimeout 延迟注册，避免当前点击立即触发
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openActionMenu]);

  useEffect(() => {
    fetchData();
  }, [schoolId, year, classId, currentPage, searchTerm, sortBy, selectedClassId]);

  useEffect(() => {
    if (year && schoolId) {
      fetchClasses();
    }
  }, [year, schoolId]);

  const fetchData = async () => {
    if (!schoolId) return;

    setLoading(true);
    try {
      // 获取学校信息
      const schoolData = await schoolService.getSchool(parseInt(schoolId));
      setSchool(schoolData);

      // 如果有classId，获取班级信息
      if (classId) {
        const classData = await classService.getClass(classId);
        setCurrentClass(classData);
      }

      // 获取学生列表
      const response = await studentService.getStudents({
        schoolId: parseInt(schoolId),
        classId: selectedClassId || classId,
        search: searchTerm || undefined,
        page: currentPage,
        size: 20,
        sort: sortBy,
      });
      setStudents(response.content);
      setTotalPages(response.totalPages);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || '获取学生列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    if (!schoolId || !year) return;
    try {
      const response = await classService.getClasses({
        schoolId: parseInt(schoolId),
        year: year,
        page: 0,
        size: 100,
      });
      setAllClasses(response.content);
    } catch (err) {
      console.error('Failed to fetch classes:', err);
    }
  };

  const handleCreate = () => {
    setEditingStudent(null);
    setFormData({
      idCardNumber: '',
      name: '',
      studentNumber: '',
      gender: undefined,
      birthDate: '',
      phone: '',
      parentName: '',
      parentPhone: '',
      schoolId: parseInt(schoolId || '0'),
      classId: selectedClassId || classId || '',
      notes: '',
    });
    setShowModal(true);
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    setFormData({
      idCardNumber: student.idCardNumber,
      name: student.name,
      studentNumber: student.studentNumber || '',
      gender: student.gender,
      birthDate: student.birthDate || '',
      phone: student.phone || '',
      parentName: student.parentName || '',
      parentPhone: student.parentPhone || '',
      schoolId: student.schoolId,
      classId: student.classId || '',
      notes: student.notes || '',
    });
    setShowModal(true);
  };

  const handleDelete = (student: Student) => {
    setRemoveTarget(student);
    setShowRemoveConfirm(true);
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    const currentClassId = selectedClassId || classId;
    try {
      if (currentClassId) {
        await studentService.removeFromClass(removeTarget.id, currentClassId);
      } else {
        await studentService.deleteStudent(removeTarget.id);
      }
      setShowRemoveConfirm(false);
      setRemoveTarget(null);
      fetchData();
    } catch (err: any) {
      showToast(err.response?.data?.message || (currentClassId ? '移出班级失败' : '删除学生失败'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingStudent) {
        await studentService.updateStudent(editingStudent.id, formData);
      } else {
        await studentService.createStudent(formData);
      }
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      showToast(err.response?.data?.message || '保存学生失败', 'error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value === '' ? undefined : value }));
  };

  const handleClassFilterSelect = (clsId: string | undefined) => {
    setSelectedClassId(clsId);
    setCurrentPage(0);
    setShowClassFilter(false);
  };

  const getBackLink = () => {
    if (classId) {
      return `/schools/${schoolId}/years/${year}/classes`;
    } else if (year) {
      return `/schools/${schoolId}/years/${year}/classes`;
    } else {
      return `/schools/${schoolId}/years`;
    }
  };

  const getBackText = () => {
    if (classId) {
      return '返回班级列表';
    } else if (year) {
      return '返回班级列表';
    } else {
      return '返回学年列表';
    }
  };

  const getPageTitle = () => {
    if (currentClass) {
      return `${currentClass.name} - 学生名单`;
    } else if (year) {
      return `${school?.name} - ${year}学年学生`;
    } else {
      return `${school?.name} - 学生管理`;
    }
  };

  // 密码管理相关
  const handleResetPasswordClick = (student: Student) => {
    setResetTarget(student);
    setShowResetConfirm(true);
  };

  const handleResetPasswordConfirm = async () => {
    if (!resetTarget) return;
    try {
      await studentService.resetPassword(resetTarget.id);
      showToast('密码重置成功', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message || '密码重置失败', 'error');
    } finally {
      setShowResetConfirm(false);
      setResetTarget(null);
    }
  };

  const handleChangePasswordClick = (student: Student) => {
    setSelectedStudent(student);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;

    if (newPassword.length < 6) {
      showToast('密码长度不能少于6位', 'warning');
      return;
    }

    try {
      await studentService.changePassword(selectedStudent.id, newPassword);
      showToast('密码修改成功', 'success');
      setShowPasswordModal(false);
    } catch (err: any) {
      showToast(err.response?.data?.message || '密码修改失败', 'error');
    }
  };

  const handleViewEnrollments = async (student: Student) => {
    setEnrollmentStudent(student);
    setShowEnrollmentModal(true);
    setShowAddEnrollment(false);
    setLoadingEnrollments(true);
    try {
      const data = await studentService.getEnrollments(student.id);
      setEnrollments(data);
    } catch (err: any) {
      showToast(err.response?.data?.message || '获取就读历史失败', 'error');
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const handleAddEnrollment = async () => {
    if (!enrollmentStudent || !enrollmentForm.classId || !enrollmentForm.academicYear) return;
    try {
      await studentService.addEnrollment(enrollmentStudent.id, enrollmentForm);
      setShowAddEnrollment(false);
      setEnrollmentForm({ classId: '', academicYear: '', notes: '' });
      // Refresh enrollments
      const data = await studentService.getEnrollments(enrollmentStudent.id);
      setEnrollments(data);
      fetchData(); // Refresh student list too
    } catch (err: any) {
      showToast(err.response?.data?.message || '添加就读记录失败', 'error');
    }
  };

  // 关联已有学生相关
  const handleAssociateClick = () => {
    setAssociateIdCard('');
    setAssociateStudent(null);
    setAssociateError('');
    setShowAssociateModal(true);
  };

  const handleAssociateSearch = async () => {
    if (!associateIdCard.trim()) return;
    setAssociateSearching(true);
    setAssociateError('');
    setAssociateStudent(null);
    try {
      const student = await studentService.getStudentByIdCard(associateIdCard.trim());
      setAssociateStudent(student);
    } catch (err: any) {
      setAssociateError(err.response?.data?.message || '未找到该身份证号对应的学生');
    } finally {
      setAssociateSearching(false);
    }
  };

  const handleAssociateConfirm = async () => {
    if (!associateStudent) return;
    const targetClassId = selectedClassId || classId;
    if (!targetClassId) {
      showToast('请先选择目标班级', 'warning');
      return;
    }
    const targetClass = allClasses.find(c => c.classId === targetClassId);
    const academicYear = targetClass?.academicYear || year || '';

    setAssociating(true);
    try {
      await studentService.addEnrollment(associateStudent.id, {
        classId: targetClassId,
        academicYear,
        notes: '通过关联已有学生功能添加',
      });
      setShowAssociateModal(false);
      fetchData();
    } catch (err: any) {
      showToast(err.response?.data?.message || '关联失败', 'error');
    } finally {
      setAssociating(false);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(getBackLink())}
          className="font-label text-sm text-on-surface-variant hover:text-on-surface mb-4 flex items-center space-x-1"
        >
          <span>←</span>
          <span>{getBackText()}</span>
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-headline font-bold text-on-surface">{getPageTitle()}</h1>
            <p className="font-label text-sm text-on-surface-variant mt-1">管理学生信息</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleAssociateClick}
              className="px-4 py-2.5 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low transition-colors font-medium shadow-soft flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span>关联已有学生</span>
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl hover:shadow-soft-md transition-all font-medium shadow-teal flex items-center space-x-2"
            >
              <PlusIcon size={18} />
              <span>添加学生</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-surface-container-lowest rounded-2xl shadow-soft p-4 mb-6">
        <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-3 md:space-y-0 md:space-x-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索学生姓名或学号..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
                className="font-body w-full pl-10 pr-4 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
              <div className="absolute left-3 top-3 text-on-surface-variant">
                <SearchIcon size={18} />
              </div>
            </div>
          </div>

          {/* Sort */}
          <div className="flex-shrink-0">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="font-body w-full md:w-auto px-4 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
            >
              <option value="name,asc">姓名 A-Z</option>
              <option value="name,desc">姓名 Z-A</option>
              <option value="studentNumber,asc">学号升序</option>
              <option value="studentNumber,desc">学号降序</option>
              <option value="createdAt,desc">最新添加</option>
              <option value="createdAt,asc">最早添加</option>
            </select>
          </div>

          {/* Class Filter */}
          {!classId && allClasses.length > 0 && (
            <div className="flex-shrink-0 relative">
              <button
                onClick={() => setShowClassFilter(!showClassFilter)}
                className="w-full md:w-auto px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium flex items-center space-x-2"
              >
                <UsersIcon size={18} />
                <span>
                  {selectedClassId
                    ? allClasses.find((c) => c.classId === selectedClassId)?.name || '按班级筛选'
                    : '全部班级'}
                </span>
              </button>

              {showClassFilter && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-surface-container-lowest rounded-2xl shadow-soft-md z-10 max-h-96 overflow-y-auto">
                  <div className="p-2">
                    <button
                      onClick={() => handleClassFilterSelect(undefined)}
                      className={`font-body w-full text-left px-3 py-2 rounded-xl hover:bg-surface-container-low transition-colors ${
                        !selectedClassId ? 'bg-primary-50 text-primary-700 font-medium' : ''
                      }`}
                    >
                      全部班级
                    </button>
                    {allClasses.map((cls) => (
                      <button
                        key={cls.classId}
                        onClick={() => handleClassFilterSelect(cls.classId)}
                        className={`font-body w-full text-left px-3 py-2 rounded-xl hover:bg-surface-container-low transition-colors ${
                          selectedClassId === cls.classId
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : ''
                        }`}
                      >
                        {cls.name}
                        <span className="font-label text-sm text-on-surface-variant ml-2">
                          ({cls.studentCount}人)
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-danger-50 text-danger-800 px-4 py-3 rounded-2xl shadow-soft mb-6">
          {error}
        </div>
      )}

      {/* Students Table */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="text-center py-12 font-body text-on-surface-variant">加载中...</div>
        ) : students.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-on-surface-variant opacity-40 mb-4 flex justify-center">
              <UsersIcon size={48} />
            </div>
            <p className="font-body text-on-surface-variant">暂无学生数据</p>
            <button
              onClick={handleCreate}
              className="mt-4 px-4 py-2 gradient-primary text-on-primary rounded-xl hover:shadow-soft-md transition-all text-sm font-medium shadow-teal"
            >
              添加第一名学生
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-surface-container-low">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-label font-medium text-on-surface-variant uppercase tracking-wider">
                      学生信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-label font-medium text-on-surface-variant uppercase tracking-wider">
                      身份证号
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-label font-medium text-on-surface-variant uppercase tracking-wider">
                      班级
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-label font-medium text-on-surface-variant uppercase tracking-wider">
                      性别/出生日期
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-label font-medium text-on-surface-variant uppercase tracking-wider">
                      联系方式
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-label font-medium text-on-surface-variant uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface-container-lowest divide-y divide-gray-100">
                  {students.map((student) => (
                    <tr key={student.id} className="hover:bg-surface-container-low transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-medium mr-3">
                            {student.name.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium font-body text-on-surface">{student.name}</div>
                            {student.studentNumber && (
                              <div className="text-sm font-label text-on-surface-variant">学号: {student.studentNumber}</div>
                            )}
                            {student.hasUserAccount && (
                              <div className="text-xs font-label text-green-600 mt-1">✓ 已开通账号</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-body text-on-surface font-mono">{student.idCardNumber}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-body text-on-surface">{student.className || '-'}</div>
                        {student.grade && (
                          <div className="text-xs font-label text-on-surface-variant">{getGradeDisplay(student.grade)}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-body text-on-surface">{student.genderDisplay || '-'}</div>
                        {student.birthDate && (
                          <div className="text-sm font-label text-on-surface-variant">{student.birthDate}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-label text-on-surface-variant">{student.phone || '-'}</div>
                        <div className="text-sm font-label text-on-surface-variant">{student.parentName || '-'}</div>
                        <div className="text-sm font-label text-on-surface-variant">{student.parentPhone || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={(e) => {
                            if (openActionMenu === student.id) {
                              setOpenActionMenu(null);
                              return;
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            setActionMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            setOpenActionMenu(student.id);
                          }}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium font-label text-on-surface bg-surface-container-lowest shadow-soft rounded-xl hover:bg-surface-container-low transition-colors"
                        >
                          操作
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="bg-surface-container-low px-6 py-4 flex items-center justify-between">
                <div className="text-sm font-label text-on-surface-variant">共 {totalPages} 页</div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                    className="px-4 py-2 bg-surface-container-lowest shadow-soft rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-low transition-colors text-sm font-medium font-label"
                  >
                    上一页
                  </button>
                  <div className="px-4 py-2 bg-surface-container-lowest shadow-soft rounded-xl text-sm font-medium font-label">
                    {currentPage + 1} / {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-4 py-2 bg-surface-container-lowest shadow-soft rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-low transition-colors text-sm font-medium font-label"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 操作下拉菜单（fixed 定位，避免被 overflow 截断） */}
      {openActionMenu !== null && (() => {
        const activeStudent = students.find(s => s.id === openActionMenu);
        if (!activeStudent) return null;
        return (
          <div
            className="fixed w-36 bg-surface-container-lowest rounded-2xl shadow-soft-md py-1 z-50"
            style={{ top: actionMenuPos.top, right: actionMenuPos.right }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => { setOpenActionMenu(null); handleEdit(activeStudent); }}
              className="w-full text-left px-4 py-2 text-sm font-body text-on-surface hover:bg-surface-container-low"
            >
              编辑
            </button>
            <button
              onClick={() => { setOpenActionMenu(null); handleViewEnrollments(activeStudent); }}
              className="w-full text-left px-4 py-2 text-sm font-body text-on-surface hover:bg-surface-container-low"
            >
              就读历史
            </button>
            {activeStudent.hasUserAccount && (
              <>
                <button
                  onClick={() => { setOpenActionMenu(null); handleResetPasswordClick(activeStudent); }}
                  className="w-full text-left px-4 py-2 text-sm font-body text-on-surface hover:bg-surface-container-low"
                >
                  重置密码
                </button>
                <button
                  onClick={() => { setOpenActionMenu(null); handleChangePasswordClick(activeStudent); }}
                  className="w-full text-left px-4 py-2 text-sm font-body text-on-surface hover:bg-surface-container-low"
                >
                  修改密码
                </button>
              </>
            )}
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={() => { setOpenActionMenu(null); handleDelete(activeStudent); }}
              className="w-full text-left px-4 py-2 text-sm font-body text-red-600 hover:bg-red-50"
            >
              {(selectedClassId || classId) ? '移出班级' : '删除'}
            </button>
          </div>
        );
      })()}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl max-w-2xl w-full shadow-soft-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 sticky top-0 bg-surface-container-lowest rounded-t-3xl">
              <h2 className="text-xl font-headline font-semibold text-on-surface">
                {editingStudent ? '编辑学生' : '添加学生'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-label font-medium text-on-surface mb-2">
                    身份证号码 <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="idCardNumber"
                    required
                    value={formData.idCardNumber}
                    onChange={handleChange}
                    className="font-body w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 font-mono"
                    placeholder="请输入身份证号码"
                    maxLength={18}
                    disabled={!!editingStudent}
                  />
                  <p className="font-label text-xs text-on-surface-variant mt-1">出生日期和性别将自动从身份证提取</p>
                </div>
                <div>
                  <label className="block text-sm font-label font-medium text-on-surface mb-2">
                    姓名 <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="font-body w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="请输入学生姓名"
                  />
                </div>
                <div>
                  <label className="block text-sm font-label font-medium text-on-surface mb-2">
                    学号
                  </label>
                  <input
                    type="text"
                    name="studentNumber"
                    value={formData.studentNumber}
                    onChange={handleChange}
                    className="font-body w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="请输入学号（可选）"
                  />
                </div>
                <div>
                  <label className="block text-sm font-label font-medium text-on-surface mb-2">
                    学生电话
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="font-body w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="请输入电话"
                  />
                </div>
                <div>
                  <label className="block text-sm font-label font-medium text-on-surface mb-2">
                    班级 <span className="text-danger-500">*</span>
                  </label>
                  <select
                    name="classId"
                    required
                    value={formData.classId || ''}
                    onChange={handleChange}
                    className="font-body w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="">请选择班级</option>
                    {allClasses.map((cls) => (
                      <option key={cls.classId} value={cls.classId}>
                        {cls.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-label font-medium text-on-surface mb-2">
                    家长姓名
                  </label>
                  <input
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleChange}
                    className="font-body w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="请输入家长姓名"
                  />
                </div>
                <div>
                  <label className="block text-sm font-label font-medium text-on-surface mb-2">
                    家长电话
                  </label>
                  <input
                    type="tel"
                    name="parentPhone"
                    value={formData.parentPhone}
                    onChange={handleChange}
                    className="font-body w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="请输入家长电话"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-label font-medium text-on-surface mb-2">
                    备注
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="font-body w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 resize-none"
                    placeholder="请输入备注"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium font-label"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl hover:shadow-soft-md transition-all font-medium font-label shadow-teal"
                >
                  {editingStudent ? '更新' : '创建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 就读历史模态框 */}
      {showEnrollmentModal && enrollmentStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl max-w-2xl w-full shadow-soft-md max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-headline font-semibold text-on-surface">
                  就读历史 - {enrollmentStudent.name}
                </h2>
                <p className="font-label text-sm text-on-surface-variant mt-1">查看和管理学生的班级就读记录</p>
              </div>
              <button
                onClick={() => {
                  setShowAddEnrollment(true);
                  setEnrollmentForm({ classId: '', academicYear: '', notes: '' });
                }}
                className="px-3 py-1.5 gradient-primary text-on-primary rounded-xl hover:shadow-soft-md text-sm font-medium font-label shadow-teal"
              >
                + 新增记录
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {showAddEnrollment && (
                <div className="mb-6 p-4 bg-surface-container-low rounded-2xl shadow-soft">
                  <h3 className="text-sm font-label font-medium text-on-surface mb-3">新增就读记录</h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-label text-on-surface-variant mb-1">班级 *</label>
                      <select
                        value={enrollmentForm.classId}
                        onChange={(e) => setEnrollmentForm({ ...enrollmentForm, classId: e.target.value })}
                        className="font-body w-full px-3 py-2 bg-surface-container-high rounded-xl text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                      >
                        <option value="">请选择班级</option>
                        {allClasses.map((cls) => (
                          <option key={cls.classId} value={cls.classId}>
                            {cls.name} ({cls.academicYear})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-label text-on-surface-variant mb-1">学年 *</label>
                      <input
                        type="text"
                        value={enrollmentForm.academicYear}
                        onChange={(e) => setEnrollmentForm({ ...enrollmentForm, academicYear: e.target.value })}
                        placeholder="如：2024"
                        className="font-body w-full px-3 py-2 bg-surface-container-high rounded-xl text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs font-label text-on-surface-variant mb-1">备注</label>
                    <input
                      type="text"
                      value={enrollmentForm.notes || ''}
                      onChange={(e) => setEnrollmentForm({ ...enrollmentForm, notes: e.target.value })}
                      placeholder="可选"
                      className="font-body w-full px-3 py-2 bg-surface-container-high rounded-xl text-sm focus:ring-2 focus:ring-primary-200 focus:outline-none"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setShowAddEnrollment(false)}
                      className="px-3 py-1.5 bg-surface-container-high rounded-xl text-sm font-label hover:bg-surface-container-low"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleAddEnrollment}
                      disabled={!enrollmentForm.classId || !enrollmentForm.academicYear}
                      className="px-3 py-1.5 gradient-primary text-on-primary rounded-xl text-sm font-label hover:shadow-soft-md disabled:opacity-50 shadow-teal"
                    >
                      添加
                    </button>
                  </div>
                </div>
              )}

              {loadingEnrollments ? (
                <div className="text-center font-body text-on-surface-variant py-8">加载中...</div>
              ) : enrollments.length === 0 ? (
                <div className="text-center font-body text-on-surface-variant py-8">暂无就读记录</div>
              ) : (
                <div className="space-y-3">
                  {enrollments.map((enrollment) => (
                    <div
                      key={enrollment.id}
                      className={`p-4 rounded-2xl ${
                        enrollment.isCurrent
                          ? 'bg-primary-50 shadow-soft'
                          : 'bg-surface-container-lowest shadow-soft'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium font-body text-on-surface">
                              {enrollment.className}
                            </span>
                            {enrollment.grade && (
                              <span className="text-xs font-label text-on-surface-variant">
                                ({getGradeDisplay(enrollment.grade)})
                              </span>
                            )}
                            {enrollment.isCurrent && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-label font-medium bg-primary-100 text-primary-800">
                                当前
                              </span>
                            )}
                          </div>
                          <div className="text-xs font-label text-on-surface-variant mt-1">
                            {enrollment.schoolName} | 学年: {enrollment.academicYear}
                          </div>
                          {enrollment.notes && (
                            <div className="text-xs font-label text-on-surface-variant mt-1">{enrollment.notes}</div>
                          )}
                        </div>
                        <div className="text-xs font-label text-on-surface-variant">
                          {new Date(enrollment.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowEnrollmentModal(false)}
                className="px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium font-label"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 移出班级/删除确认浮窗 */}
      {showRemoveConfirm && removeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl max-w-md w-full shadow-soft-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-xl font-headline font-semibold text-on-surface">
                {(selectedClassId || classId) ? '确认移出班级' : '确认删除学生'}
              </h2>
            </div>
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-medium mr-3">
                  {removeTarget.name.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-medium font-body text-on-surface">{removeTarget.name}</div>
                  {removeTarget.studentNumber && (
                    <div className="text-sm font-label text-on-surface-variant">学号: {removeTarget.studentNumber}</div>
                  )}
                </div>
              </div>
              {(selectedClassId || classId) ? (
                <p className="text-sm font-body text-on-surface-variant">
                  确定要将 <span className="font-medium">{removeTarget.name}</span> 从当前班级中移出吗？学生信息不会被删除，仅解除与该班级的关联。
                </p>
              ) : (
                <p className="text-sm font-body text-on-surface-variant">
                  确定要删除 <span className="font-medium">{removeTarget.name}</span> 吗？此操作将删除该学生的所有信息。
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end space-x-3">
              <button
                onClick={() => { setShowRemoveConfirm(false); setRemoveTarget(null); }}
                disabled={removing}
                className="px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium font-label"
              >
                取消
              </button>
              <button
                onClick={handleRemoveConfirm}
                disabled={removing}
                className="px-4 py-2.5 bg-danger-600 text-white rounded-xl hover:bg-danger-700 transition-colors font-medium font-label disabled:opacity-50"
              >
                {removing ? '处理中...' : (selectedClassId || classId) ? '确认移出' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 密码重置确认模态框 */}
      {showResetConfirm && resetTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl max-w-md w-full shadow-soft-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-xl font-headline font-semibold text-on-surface">确认重置密码</h2>
            </div>
            <div className="p-6">
              <p className="text-sm font-body text-on-surface-variant">
                确定要重置 <span className="font-medium">{resetTarget.name}</span> 的密码吗？密码将重置为身份证后6位。
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end space-x-3">
              <button
                onClick={() => { setShowResetConfirm(false); setResetTarget(null); }}
                className="px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium font-label"
              >
                取消
              </button>
              <button
                onClick={handleResetPasswordConfirm}
                className="px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors font-medium font-label"
              >
                确认重置
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 关联已有学生模态框 */}
      {showAssociateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl max-w-lg w-full shadow-soft-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-xl font-headline font-semibold text-on-surface">关联已有学生</h2>
              <p className="font-label text-sm text-on-surface-variant mt-1">输入身份证号查询已有学生，关联到当前班级</p>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-label font-medium text-on-surface mb-2">
                  身份证号码
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={associateIdCard}
                    onChange={(e) => setAssociateIdCard(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAssociateSearch(); } }}
                    className="font-body flex-1 px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 font-mono"
                    placeholder="请输入身份证号码"
                    maxLength={18}
                  />
                  <button
                    onClick={handleAssociateSearch}
                    disabled={!associateIdCard.trim() || associateSearching}
                    className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl hover:shadow-soft-md transition-all font-medium font-label disabled:opacity-50 disabled:cursor-not-allowed shadow-teal"
                  >
                    {associateSearching ? '查询中...' : '查询'}
                  </button>
                </div>
              </div>

              {associateError && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-2xl shadow-soft mb-4 text-sm font-body">
                  {associateError}
                </div>
              )}

              {associateStudent && (
                <div className="bg-primary-50 rounded-2xl shadow-soft p-4 mb-4">
                  <h3 className="text-sm font-label font-medium text-primary-900 mb-3">查询到的学生信息</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm font-body">
                    <div>
                      <span className="text-on-surface-variant">姓名：</span>
                      <span className="font-medium text-on-surface">{associateStudent.name}</span>
                    </div>
                    <div>
                      <span className="text-on-surface-variant">性别：</span>
                      <span className="font-medium text-on-surface">{associateStudent.genderDisplay || '-'}</span>
                    </div>
                    <div>
                      <span className="text-on-surface-variant">学校：</span>
                      <span className="font-medium text-on-surface">{associateStudent.schoolName}</span>
                    </div>
                    <div>
                      <span className="text-on-surface-variant">当前班级：</span>
                      <span className="font-medium text-on-surface">{associateStudent.className || '无'}</span>
                    </div>
                    {associateStudent.studentNumber && (
                      <div>
                        <span className="text-on-surface-variant">学号：</span>
                        <span className="font-medium text-on-surface">{associateStudent.studentNumber}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-on-surface-variant">身份证：</span>
                      <span className="font-medium text-on-surface font-mono text-xs">{associateStudent.idCardNumber}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-primary-200">
                    <p className="text-xs font-label text-primary-700">
                      将关联到：{allClasses.find(c => c.classId === (selectedClassId || classId))?.name || '当前班级'}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAssociateModal(false)}
                  className="px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium font-label"
                >
                  取消
                </button>
                {associateStudent && (
                  <button
                    onClick={handleAssociateConfirm}
                    disabled={associating}
                    className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl hover:shadow-soft-md transition-all font-medium font-label disabled:opacity-50 shadow-teal"
                  >
                    {associating ? '关联中...' : '确认关联'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 密码修改模态框 */}
      {showPasswordModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl max-w-md w-full shadow-soft-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-xl font-headline font-semibold text-on-surface">
                修改密码 - {selectedStudent.name}
              </h2>
            </div>
            <form onSubmit={handleChangePasswordSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-label font-medium text-on-surface mb-2">
                  新密码 <span className="text-danger-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="font-body w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                  placeholder="请输入新密码（至少6位）"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium font-label"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl hover:shadow-soft-md transition-all font-medium font-label shadow-teal"
                >
                  确认修改
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentListPage;
